import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TipoArredondamentoEnum } from '@shared/enums/formula/tipo-arredondamento.enum';
import { FormulaTokenTipoEnum } from '@shared/enums/formula/formula-token-tipo.enum';
import { MaskService } from '@shared/services/mask.service';
import { VariavelStruct } from '@shared/services/structs/formula/variavel.struct';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { VariaveisDisponiveisSectionComponent } from '@shared/components/formula/variaveis-disponiveis-section/variaveis-disponiveis-section.component';
import { ExpressionToken, FormulaExpressaoEditorComponent } from '../formula-expressao-editor/formula-expressao-editor.component';
import { classeBadgeFuncao, classeBadgeTokenFormula, classeBadgeVariavel, FORMULA_BADGE_CLASSES, isVariavelParametro } from '../utils/formula-badge.util';
import { tokenizarExpressaoFormula } from '../utils/formula-expressao.util';
import { FormulaEditorSectionValue } from './formula-editor-section-value';

export { FormulaEditorSectionValue } from './formula-editor-section-value';

@Component({
    selector: 'app-formula-editor-section',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatButtonToggleModule,
        MatDatepickerModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatTooltipModule,
        AlertCardComponent,
        FormulaExpressaoEditorComponent,
        VariaveisDisponiveisSectionComponent,
    ],
    templateUrl: './formula-editor-section.component.html',
    styleUrl: './formula-editor-section.component.css',
})
export class FormulaEditorSectionComponent implements OnChanges {
    public readonly badgeClasses = FORMULA_BADGE_CLASSES;
    public readonly classeBadgeFuncao = classeBadgeFuncao;
    public readonly classeBadgeTokenFormula = classeBadgeTokenFormula;
    @Input() expressao = '';
    @Input() variaveis: VariavelStruct[] = [];
    @Input() tipoArredondamento: TipoArredondamentoEnum = TipoArredondamentoEnum.Arredondar;
    @Input() casasDecimais = 2;
    @Input() dataVigenciaInicio: string | null = null;
    @Input() dataVigenciaFim: string | null = null;
    @Input() mostrarVigencia = true;
    @Output() editorChange = new EventEmitter<FormulaEditorSectionValue>();

    @ViewChild(FormulaExpressaoEditorComponent) private expressaoEditor?: FormulaExpressaoEditorComponent;

    /** True quando a expressão está inconsistente (editor já valida token a token; aqui só refletimos o resultado). */
    public get expressaoInvalida(): boolean {
        return this.expressaoEditor?.validacao.valida === false;
    }

    public readonly TipoArredondamentoEnum = TipoArredondamentoEnum;

    public showAllVariaveis = false;
    public showAllOperadores = false;
    public variaveisAcessoRapido: VariavelStruct[] = [];
    public vigenciaInicioDate: Date | null = null;
    public vigenciaFimDate: Date | null = null;

    /** Data mínima permitida para a vigência início: não pode ser anterior a hoje. */
    public readonly minVigenciaInicio: Date = this.inicioDeHoje();

    public readonly operadoresAritmeticos: ExpressionToken[] = [
        { tipo: FormulaTokenTipoEnum.Operador, valor: ' + ', display: '+' },
        { tipo: FormulaTokenTipoEnum.Operador, valor: ' - ', display: '-' },
        { tipo: FormulaTokenTipoEnum.Operador, valor: ' * ', display: 'x' },
        { tipo: FormulaTokenTipoEnum.Operador, valor: ' / ', display: '/' },
    ];

    public readonly operadoresAgrupamento: ExpressionToken[] = [
        { tipo: FormulaTokenTipoEnum.Parentese, valor: '(', display: '(' },
        { tipo: FormulaTokenTipoEnum.Parentese, valor: ')', display: ')' },
    ];

    public readonly operadoresComparacao: ExpressionToken[] = [
        { tipo: FormulaTokenTipoEnum.Operador, valor: ' < ', display: '<' },
        { tipo: FormulaTokenTipoEnum.Operador, valor: ' > ', display: '>' },
        { tipo: FormulaTokenTipoEnum.Operador, valor: ' <= ', display: '<=' },
        { tipo: FormulaTokenTipoEnum.Operador, valor: ' >= ', display: '>=' },
        { tipo: FormulaTokenTipoEnum.Operador, valor: ' <> ', display: '<>' },
        { tipo: FormulaTokenTipoEnum.Operador, valor: ' = ', display: '=' },
    ];

    public readonly operadoresLogicos: ExpressionToken[] = [
        { tipo: FormulaTokenTipoEnum.Operador, valor: ' e ', display: 'e' },
        { tipo: FormulaTokenTipoEnum.Operador, valor: ' ou ', display: 'ou' },
    ];

    private readonly tooltipsOperadores: Record<string, string> = {
        '+': 'Soma',
        '-': 'Subtração',
        '*': 'Multiplicação',
        '/': 'Divisão',
        '(': 'Parêntese esquerdo',
        ')': 'Parêntese direito',
        '<': 'Compara se um argumento é menor que o outro',
        '>': 'Compara se um argumento é maior que o outro',
        '<=': 'Compara se um argumento é menor ou igual ao outro',
        '>=': 'Compara se um argumento é maior ou igual ao outro',
        '<>': 'Compara se os argumentos são diferentes',
        '=': 'Compara se os argumentos são iguais',
        e: 'Retorna verdadeiro quando os dois argumentos são verdadeiros',
        ou: 'Retorna verdadeiro quando pelo menos um argumento é verdadeiro',
    };

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['expressao'] || changes['variaveis']) {
            this.atualizarVariaveisAcessoRapido();
        }

        if (changes['casasDecimais']) {
            this.casasDecimais = this.normalizarCasasDecimais(this.casasDecimais);
        }

        if (changes['dataVigenciaInicio']) {
            this.vigenciaInicioDate = this.parseData(this.dataVigenciaInicio);
        }

        if (changes['dataVigenciaFim']) {
            this.vigenciaFimDate = this.parseData(this.dataVigenciaFim);
        }
    }

    public inserirVariavel(variavel: VariavelStruct): void {
        this.expressaoEditor?.inserirVariavel(variavel);
    }

    public inserirToken(token: ExpressionToken): void {
        this.expressaoEditor?.inserirToken(token);
    }

    public tooltipOperador(token: ExpressionToken): string {
        return this.tooltipsOperadores[token.valor.trim().toLowerCase()] ?? '';
    }

    public inserirParentesesAgrupamento(): void {
        this.expressaoEditor?.inserirParentesesAgrupamento();
    }

    public inserirBlocoSe(): void {
        this.expressaoEditor?.inserirBlocoSe();
    }

    /** Insere o agregador permitido nas fórmulas documentais do Motor Fiscal. */
    public inserirBlocoSoma(): void {
        this.expressaoEditor?.inserirBlocoSoma();
    }

    /** Insere o rateio disponível para fórmulas de item. */
    public inserirBlocoRateio(): void {
        this.expressaoEditor?.inserirBlocoRateio();
    }

    /** Insere uma constante numérica como token editável inline (acesso rápido). */
    public inserirConstante(): void {
        this.expressaoEditor?.inserirConstanteEditavel();
    }

    public variavelChave(variavel: VariavelStruct): string {
        return variavel.chave || variavel.tagXml || variavel.nome;
    }

    public classeVariavel(variavel: VariavelStruct): string {
        return classeBadgeVariavel(variavel);
    }

    public readonly isVariavelParametro = isVariavelParametro;

    public onExpressaoChange(expressao: string): void {
        this.expressao = expressao;
        this.atualizarVariaveisAcessoRapido();
        this.emitir();
    }

    public onArredondamentoChange(tipoArredondamento: TipoArredondamentoEnum): void {
        this.tipoArredondamento = tipoArredondamento;
        this.emitir();
    }

    public alterarCasasDecimais(delta: number): void {
        const proximo = this.normalizarCasasDecimais(this.casasDecimais + delta);
        if (proximo === this.casasDecimais) return;

        this.casasDecimais = proximo;
        this.emitir();
    }

    public onVigenciaChange(campo: 'inicio' | 'fim', valor: Date | null): void {
        if (campo === 'inicio') {
            let data = this.normalizarData(valor);
            // Trava: vigência início nunca pode ser anterior a hoje.
            if (data && data < this.minVigenciaInicio) data = new Date(this.minVigenciaInicio);
            this.vigenciaInicioDate = data;
        } else {
            this.vigenciaFimDate = this.normalizarData(valor);
        }

        if (this.vigenciaInicioDate && this.vigenciaFimDate && this.vigenciaFimDate < this.vigenciaInicioDate) {
            this.vigenciaFimDate = null;
        }

        this.emitir();
    }

    public onDateInput(event: Event): void {
        MaskService.onDateInput(event);
    }

    public onDateKeydown(event: KeyboardEvent): void {
        const teclasPermitidas = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter'];
        const isAtalho = (event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase());
        if (teclasPermitidas.includes(event.key) || isAtalho) return;
        if (!/^\d$/.test(event.key)) {
            event.preventDefault();
        }
    }

    public onVigenciaBlur(event: FocusEvent, campo: 'inicio' | 'fim'): void {
        const input = event.target as HTMLInputElement;
        const valor = input?.value?.trim() || '';
        if (!valor) {
            this.onVigenciaChange(campo, null);
            return;
        }

        const apenasNumeros = valor.replace(/\D/g, '').substring(0, 8);
        if (apenasNumeros.length !== 8) {
            input.value = '';
            this.onVigenciaChange(campo, null);
            return;
        }

        const formatado = `${apenasNumeros.substring(0, 2)}/${apenasNumeros.substring(2, 4)}/${apenasNumeros.substring(4, 8)}`;
        input.value = formatado;
        this.onVigenciaChange(campo, this.parseDataBr(formatado));
    }

    private emitir(): void {
        this.editorChange.emit({
            expressao: this.expressao,
            tipoArredondamento: this.tipoArredondamento,
            casasDecimais: this.casasDecimais,
            dataVigenciaInicio: this.formatarDataIso(this.vigenciaInicioDate),
            dataVigenciaFim: this.formatarDataIso(this.vigenciaFimDate),
        });
    }

    private atualizarVariaveisAcessoRapido(): void {
        const variaveisPorChave = new Map(
            this.variaveis.map(variavel => [this.variavelChave(variavel).toLowerCase(), variavel]),
        );
        const chavesIncluidas = new Set<string>();
        const resultado: VariavelStruct[] = [];

        for (const token of tokenizarExpressaoFormula(this.expressao)) {
            if (token.tipo !== FormulaTokenTipoEnum.Variavel) continue;

            const chave = token.valor.slice(1, -1).toLowerCase();
            if (chavesIncluidas.has(chave)) continue;

            const variavel = variaveisPorChave.get(chave);
            if (!variavel) continue;

            chavesIncluidas.add(chave);
            resultado.push(variavel);
        }

        this.variaveisAcessoRapido = resultado;
    }

    private normalizarCasasDecimais(valor: number | null | undefined): number {
        if (typeof valor !== 'number' || !Number.isFinite(valor)) return 2;
        return Math.min(6, Math.max(1, Math.trunc(valor)));
    }

    private parseData(valor: string | null | undefined): Date | null {
        if (!valor) return null;

        const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor);
        if (iso) {
            return this.criarData(Number(iso[1]), Number(iso[2]), Number(iso[3]));
        }

        return this.normalizarData(new Date(valor));
    }

    private parseDataBr(valor: string): Date | null {
        const partes = valor.split('/');
        if (partes.length !== 3) return null;
        return this.criarData(Number(partes[2]), Number(partes[1]), Number(partes[0]));
    }

    private criarData(ano: number, mes: number, dia: number): Date | null {
        if (!Number.isInteger(dia) || !Number.isInteger(mes) || !Number.isInteger(ano)) return null;
        if (ano < 1000 || ano > 9999 || mes < 1 || mes > 12 || dia < 1 || dia > 31) return null;

        const data = new Date(ano, mes - 1, dia);
        data.setHours(0, 0, 0, 0);
        if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== dia) return null;
        return data;
    }

    private inicioDeHoje(): Date {
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        return hoje;
    }

    private normalizarData(valor: Date | null): Date | null {
        if (!valor) return null;
        const data = new Date(valor);
        if (Number.isNaN(data.getTime())) return null;
        data.setHours(0, 0, 0, 0);
        return data;
    }

    private formatarDataIso(data: Date | null): string | null {
        if (!data) return null;
        const ano = data.getFullYear();
        const mes = String(data.getMonth() + 1).padStart(2, '0');
        const dia = String(data.getDate()).padStart(2, '0');
        return `${ano}-${mes}-${dia}`;
    }
}
